<template>
    <v-container fluid grid-list-lg>
        <v-progress-linear indeterminate v-if="loading"/>
        <v-layout v-else>
            <v-flex md4>
                <div class="headline pb-2">Endringsmelding</div>
                <v-card class="pa-3">
                    <v-text-field label="Intern beskrivelse" class="mb-2" v-model="amendment.title" outlined
                                  persistent-hint
                                  hint="Til internt bruk. Vises i lista over endringsmeldinger. Oppsummer endringen med noen få ord."/>

                    <v-layout class="mt-2">
                        <v-flex sm12>
                            <ProjectSelect label="Knytt mot prosjekt" v-model="amendment.project" outlined
                                           hide-details/>
                        </v-flex>
                    </v-layout>

                    <v-layout>
                        <v-flex shrink>
                            <v-checkbox label="Masseavregning" hide-details class="mt-0"
                                        v-model="amendment.is_extra_material"/>
                        </v-flex>
                        <v-flex shrink>
                            <v-checkbox label="Tilleggsarbeid" hide-details class="mt-0"
                                        v-model="amendment.is_extra_work"/>
                        </v-flex>
                        <v-flex>
                            <v-checkbox label="Prisstigningsnota" hide-details class="mt-0"
                                        v-model="amendment.is_price_adjustment"/>
                        </v-flex>
                    </v-layout>

                    <v-layout class="mt-2">
                        <v-flex sm6>
                            <DatePickerField label="Dato varslet" v-model="amendment.date"/>
                            <v-btn v-if="amendment.date" text color="red" small @click="amendment.date = null">Fjern
                                dato
                            </v-btn>
                        </v-flex>
                        <v-flex sm6>
                            <DatePickerField label="Dato revidert" v-model="amendment.date_revised"/>
                            <v-btn v-if="amendment.date_revised" text color="red" small
                                   @click="amendment.date_revised = null">Fjern dato
                            </v-btn>
                        </v-flex>
                    </v-layout>
                    <v-layout>
                        <v-flex sm6>
                            <v-text-field outlined hide-details v-model="amendment.customer_ref" class="pb-3"
                                          label="Prosjektleder"/>
                        </v-flex>
                        <v-flex sm6>
                            <v-text-field outlined label="Epost" v-model="amendment.customer_email" persistent-hint
                                          hint="Adressen endringsmeldingen sendes til."/>
                        </v-flex>
                    </v-layout>
                </v-card>
            </v-flex>
            <v-flex>
                <div class="headline pb-2">Beskrivelse av endring</div>

                <v-card class="mb-3 pa-3">
                    <v-layout>
                        <v-flex md6>
                            <v-textarea outlined label="Beskrivelse av endring" v-model="amendment.description"
                                        hide-details/>
                        </v-flex>
                        <v-flex md3>
                            <v-textarea outlined label="Årsak" v-model="amendment.reason" hide-details/>
                        </v-flex>
                        <v-flex md3>
                            <v-textarea outlined label="Andre konsekvenser / merknader" v-model="amendment.note"
                                        hide-details/>
                        </v-flex>
                    </v-layout>
                </v-card>

                <div class="headline pb-2">Prisoverslag</div>
                <v-simple-table class="elevation-1" dense>
                    <template v-slot:default>
                        <thead>
                        <tr>
                            <th class="text-center pr-5" style="width: 84px">Tekst</th>
                            <th colspan="4">Endringsmeldingslinje</th>
                            <th class="text-right">Sum</th>
                            <th>&nbsp;</th>
                        </tr>
                        </thead>
                        <tbody>
                        <OfferRow v-for="line in amendment.lines" :value="line" @addBefore="addLineBefore"
                                  @remove="removeLine" :key="line.key"/>
                        </tbody>
                        <tfoot>
                        <tr class="amendment-row">
                            <td colspan="5">
                            </td>
                            <td class="text-right">
                                {{ formatMoney(sum) }}
                            </td>
                            <td>
                                <v-btn icon @click="addLineLast" color="green">
                                    <v-icon>add</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                        </tfoot>
                    </template>
                </v-simple-table>

                <v-layout class="pt-3 px-2">
                    <v-btn :loading="saving" color="green" dark @click="save">Lagre endringsmelding</v-btn>
                    <v-btn :loading="downloading" color="green" outlined class="ml-3" @click="save('download')">
                        Lagre og last ned PDF
                    </v-btn>
                    <v-btn :loading="sending" color="blue" outlined class="ml-3" @click="save('email')"
                           :disabled="!amendment.customer_email">
                        Lagre og send på epost
                    </v-btn>

                    <v-spacer/>

                    <v-btn :loading="deleting" color="red" outlined class="ml-3" @click="remove"
                           v-if="!!amendment.id">
                        Slett
                    </v-btn>
                </v-layout>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
    import dayjs from "dayjs";
    import {formatMoney} from "../helpers";
    import OfferRow from "../components/helpers/OfferRow";
    import DatePickerField from "../components/helpers/DatePickerField";
    import ProjectManagerSelect from "../components/helpers/ProjectManagerSelect";
    import ProjectSelect from "../components/helpers/ProjectSelect";

    let newEmptyLine = (type, sort) => {
        if (type === 'text') {
            return {
                key: Math.random(),
                type: 'text',
                text: '',
                sort: sort
            };
        }
        return {
            key: Math.random(),
            type: 'product',
            text: '',
            count: 1,
            unit: 'stk',
            price: 0,
            discount: 0,
            sum: 0,
            sort: sort
        }
    };

    const ORDER_START = 10000;

    function newEmptyAmendment() {
        return {
            project: null,
            title: '',
            date: dayjs().format('YYYY-MM-DD'),
            date_revised: null,
            customer_ref: '',
            customer_email: '',

            is_extra_material: false,
            is_extra_work: false,
            is_price_adjustment: false,

            description: '',
            reason: '',
            note: '',

            lines: [
                newEmptyLine('product', ORDER_START)
            ]
        };
    }

    export default {
        components: {
            OfferRow,
            DatePickerField,
            ProjectManagerSelect,
            ProjectSelect
        },

        data() {
            return {
                loading: false,
                amendment: null,

                saving: false,
                downloading: false,
                sending: false
            }
        },

        created() {
            if (this.amendmentId) {
                this.loadData();
            } else {
                this.amendment = newEmptyAmendment();
            }
        },

        methods: {
            formatMoney,
            loadData() {
                this.loading = true;
                this.$http.get(`amendments/${this.amendmentId}`).then(response => {
                    this.amendment = response.data;
                }).finally(() => {
                    this.loading = false;
                })
            },
            addLineBefore(after) {
                let indexBefore = this.amendment.lines.indexOf(after);
                let sort = ORDER_START;
                if (indexBefore === 0) {
                    // Add bafore first line.
                    sort = this.amendment.lines[indexBefore].sort - 1;
                } else if (indexBefore > 0) {
                    // Add between lines first line.
                    sort = after.sort + 1;
                    let before = this.amendment.lines[indexBefore - 1] || null;
                    if (after) {
                        sort = (after.sort + before.sort) / 2;
                    }
                }
                this.addLine(sort);
            },
            addLineLast() {
                this.addLine(this.amendment.lines[this.amendment.lines.length - 1].sort + 1);
            },
            addLine(insertOrder) {
                let lines = [
                    ...this.amendment.lines,
                    newEmptyLine('product', insertOrder)
                ].sort((a, b) => a.sort - b.sort);

                this.amendment.lines = lines;
            },
            removeLine(line) {
                // Always keep one line.
                if (this.amendment.lines.length <= 1) {
                    return;
                }

                let idx = this.amendment.lines.indexOf(line);
                this.amendment.lines.splice(idx, 1);
            },

            download(amendmentId) {
                this.downloading = true;
                this.$http.get(`amendments/${amendmentId}/pdf/`, {responseType: 'blob'}).then(response => {
                    let fileURL = window.URL.createObjectURL(new Blob([response.data]));

                    let fileLink = document.createElement('a');
                    fileLink.href = fileURL;
                    let filename = response.headers['content-disposition'].split('"')[1];
                    fileLink.setAttribute('download', filename);
                    document.body.appendChild(fileLink);
                    fileLink.click();

                    this.$root.addMessage('PDF lastet ned.', 'green');
                }).catch(() => {
                    this.$root.addMessage('Kunne ikke generere PDF.', 'red');
                }).finally(() => {
                    this.downloading = false;
                });
            },
            email(amendmentId) {
                let ok = confirm('Vil du sende dette endringsmeldingen på epost til kunden?');
                if (!ok) return;

                this.sending = true;
                this.$http.post(`amendments/${amendmentId}/email/`).then(response => {
                    this.$root.addMessage('Endringsmeldingen er sendt på epost.', 'green');
                }).catch(() => {
                    this.$root.addMessage('Feil under sending av endringsmelding på epost.', 'red');
                }).finally(() => {
                    this.sending = false;
                });
            },
            save(action = null) {
                console.log(action)
                let request;
                if (this.amendment.id) request = this.$http.put(`amendments/${this.amendment.id}/`, this.amendment);
                else request = this.$http.post(`amendments/`, this.amendment);

                this.saving = true;
                request.then(response => {
                    if (!this.amendment.id) {
                        let query = {};
                        if (action) {
                            query['action'] = action;
                        }
                        return this.$router.push({
                            name: 'EditAmendment',
                            params: {id: response.data.id},
                            query
                        });
                    }

                    this.amendment = response.data;
                    if (action === 'download') {
                        return this.download(this.amendment.id);
                    } else if (action === 'email') {
                        return this.email(this.amendment.id);
                    } else {
                        this.$root.addMessage('Endringsmelding lagret.', 'green');
                    }
                }).catch(() => {
                    this.$root.addMessage('Feil under lagring. Sjekk at alle nødvendige felter er fylt ut.', 'red');
                }).finally(() => {
                    this.saving = false;
                });
            },
            remove() {
                let ok = confirm('Vil du slette denne endringsmeldingen?')
                if (!ok) return;

                this.deleting = true;
                this.$http.delete(`amendments/${this.amendment.id}/`).then(response => {
                    this.$root.addMessage('Sletting vellykket.', 'green');
                    this.$router.replace({name: 'Amendments'});
                }).catch(() => {
                    this.$root.addMessage('Sletting feilet.', 'red');
                }).finally(() => {
                    this.deleting = false;
                })
            }
        },

        watch: {
            '$route.params.id'() {
                this.loadData();
            },
            '$route.query.action'(action) {
                let amendmentId = this.$route.params.id;
                switch (action) {
                    case 'download':
                        this.download(amendmentId);
                        break;
                    case 'email':
                        this.email(amendmentId);
                        break;
                    default:
                        return
                }
            }
        },

        computed: {
            amendmentId() {
                return this.$route.params.id;
            },
            sum() {
                return this.amendment.lines.reduce((acc, line) => {
                    return (parseFloat(line.sum) || 0) + acc
                }, 0).toFixed(2);
            }
        }
    }
</script>